import { CIVIL_ADVISE_CATEGORY_ID, FILE_CATEGORY_CIVIL_ADVISE } from '../pages/wizard/civilAdvise/civilAdviseConstants';
import {
  CIVIL_APPELLATE_CATEGORY_CODE,
  CIVIL_APPELLATE_CATEGORY_ID,
  isCivilAppellateCategory,
  setCivilAppellateCategoryId,
} from '../pages/wizard/civilAppellate/civilAppellateConstants';
import {
  CIVIL_LITIGATION_CATEGORY_CODE,
  CIVIL_LITIGATION_CATEGORY_ID,
  FILE_CATEGORY_CIVIL_LITIGATION,
} from '../pages/wizard/civilLitigation/civilLitigationConstants';
import { CRIMINAL_CATEGORY_CODE, CRIMINAL_CATEGORY_ID } from '../pages/wizard/criminal/criminalConstants';
import { CRIMINAL_APPELLATE_CATEGORY_ID } from '../pages/wizard/criminalAppellate/criminalAppellateConstants';
import { FILE_CATEGORY_CRIMINAL } from '../mocks/criminalMasterData';
import {
  NOMINATION_CATEGORY_ID,
  isNominationCategory,
  setNominationCategoryId,
} from '../pages/wizard/nomination/nominationConstants';
import {
  PUBLIC_PETITION_CATEGORY_ID,
  isPublicPetitionCategory,
  setPublicPetitionCategoryId,
} from '../pages/wizard/publicPetition/publicPetitionConstants';
import {
  RIGHT_TO_INFORMATION_CATEGORY_ID,
  isRightToInformationCategory,
  setRightToInformationCategoryId,
} from '../pages/wizard/rightToInformation/rightToInformationConstants';
import {
  EXTRADITION_MLA_CATEGORY_ID,
  isExtraditionMlaCategory,
  setExtraditionMlaCategoryId,
} from '../pages/wizard/extraditionMla/extraditionMlaConstants';
import {
  ATTORNEY_GENERAL_CATEGORY_ID,
  AGD_INTERNAL_CATEGORY_ID,
  isAttorneyGeneralCategory,
} from '../pages/wizard/attorneyGeneral/attorneyGeneralConstants';

export type WizardFlowKind =
  | 'criminal'
  | 'criminal-appellate'
  | 'civil-advise'
  | 'civil-litigation'
  | 'civil-appellate'
  | 'extradition-mla'
  | 'nomination-requests'
  | 'public-petition'
  | 'right-to-information'
  | 'attorney-general'
  | 'unknown';

export function wizardFlowForCategoryId(
  categoryId: string,
  categoryName?: string,
  categoryCode?: string,
): WizardFlowKind {

  const id = categoryId.trim();
  const code = (categoryCode ?? '').trim().toUpperCase();
  if (id === CIVIL_ADVISE_CATEGORY_ID) return 'civil-advise';
  if (id === CRIMINAL_APPELLATE_CATEGORY_ID) return 'criminal-appellate';
  if (id && NOMINATION_CATEGORY_ID && id === NOMINATION_CATEGORY_ID) return 'nomination-requests';
  if (id && PUBLIC_PETITION_CATEGORY_ID && id === PUBLIC_PETITION_CATEGORY_ID) return 'public-petition';
  if (id && RIGHT_TO_INFORMATION_CATEGORY_ID && id === RIGHT_TO_INFORMATION_CATEGORY_ID) {
    return 'right-to-information';
  }
  if (id === CRIMINAL_CATEGORY_ID || code === CRIMINAL_CATEGORY_CODE) return 'criminal';
  if (id && EXTRADITION_MLA_CATEGORY_ID && id === EXTRADITION_MLA_CATEGORY_ID) return 'extradition-mla';
  if (id === ATTORNEY_GENERAL_CATEGORY_ID || id === AGD_INTERNAL_CATEGORY_ID) return 'attorney-general';
  if (isExtraditionMlaCategory({ id, name: categoryName, code: categoryCode })) return 'extradition-mla';
  if (isAttorneyGeneralCategory({ id, name: categoryName, code: categoryCode })) return 'attorney-general';
  if (id && CIVIL_APPELLATE_CATEGORY_ID && id === CIVIL_APPELLATE_CATEGORY_ID) return 'civil-appellate';
  if (code === CIVIL_APPELLATE_CATEGORY_CODE || isCivilAppellateCategory({ id, name: categoryName, code: categoryCode })) {
    return 'civil-appellate';
  }
  if (id === CIVIL_LITIGATION_CATEGORY_ID || code === CIVIL_LITIGATION_CATEGORY_CODE) {
    return 'civil-litigation';
  }
  if (isNominationCategory({ id, name: categoryName, code: categoryCode })) return 'nomination-requests';
  if (isPublicPetitionCategory({ id, name: categoryName, code: categoryCode })) return 'public-petition';
  if (isRightToInformationCategory({ id, name: categoryName, code: categoryCode })) return 'right-to-information';
  return 'unknown';
}

export const WIZARD_BASE = '/requests/new';

/** Create-case-request wizard URLs whose in-progress state must not survive auth/role changes. */
export const WIZARD_FLOW_PATH_PREFIXES = [WIZARD_BASE, '/tasks/create-case-request'] as const;

export function isWizardFlowPath(pathname: string): boolean {
  const normalized = pathname.split('?')[0].replace(/\/+$/, '') || '/';
  return WIZARD_FLOW_PATH_PREFIXES.some(
    (prefix) => normalized === prefix || normalized.startsWith(`${prefix}/`),
  );
}
export const CIVIL_ADVISE_WIZARD_BASE = WIZARD_BASE;
export const CIVIL_LITIGATION_WIZARD_BASE = WIZARD_BASE;
export const CRIMINAL_WIZARD_BASE = WIZARD_BASE;

export function wizardBasePath(_categoryId?: string): string {
  return WIZARD_BASE;
}

/** Route continue-draft by list category name when id-only resume is used. */
export function wizardFlowForCategoryName(categoryName: string): WizardFlowKind {
  if (isExtraditionMlaCategory({ name: categoryName })) return 'extradition-mla';
  const trimmed = categoryName.trim();
  const n = trimmed.toLowerCase();
  // Appellate before litigation — e.g. "Civil Litigation - Appellate Courts" is Case Type 4.
  if (n.includes('appellate') && n.includes('civil')) return 'civil-appellate';
  if (n === FILE_CATEGORY_CIVIL_ADVISE.toLowerCase() || /civil\s+division\s*[-–—]?\s*advise/i.test(trimmed)) {
    return 'civil-advise';
  }
  if (
    n === FILE_CATEGORY_CIVIL_LITIGATION.toLowerCase() ||
    (n.includes('litigation') && n.includes('civil')) ||
    /civil\s+division\s*[-–—]?\s*litigation/i.test(trimmed)
  ) {
    return 'civil-litigation';
  }
  if (n.includes('advise') && n.includes('civil')) return 'civil-advise';
  if (n.includes('criminal') && n.includes('appellate')) return 'criminal-appellate';
  if (n.includes('nomination')) return 'nomination-requests';
  if (n.includes('public petition')) return 'public-petition';
  if (n.includes('right to information') || n === 'rti') return 'right-to-information';
  if (n.includes('attorney general') || n.includes('agd - internal') || n.includes('agd internal')) {
    return 'attorney-general';
  }
  if (
    n === FILE_CATEGORY_CRIMINAL.toLowerCase() ||
    n === 'criminal division' ||
    /criminal\s+division/i.test(trimmed) ||
    n.includes('criminal')
  ) {
    return 'criminal';
  }
  return 'unknown';
}

export function resolveWizardFlow(
  categoryId: string,
  categoryName?: string,
  categoryCode?: string,
): WizardFlowKind {
  const byId = wizardFlowForCategoryId(categoryId, categoryName, categoryCode);
  if (byId !== 'unknown') return byId;
  return wizardFlowForCategoryName(categoryName ?? '');
}

/**
 * Cache dynamic category UUIDs from master data so id-based flow routing works
 * when switching file category (e.g. Criminal → Public Petition).
 */
export function registerWizardCategoryIdsFromLookups(
  categories: ReadonlyArray<{ id?: string; name?: string; code?: string }>,
): void {
  for (const category of categories) {
    const id = (category.id ?? '').trim();
    if (!id) continue;
    if (isPublicPetitionCategory(category)) setPublicPetitionCategoryId(id);
    if (isRightToInformationCategory(category)) setRightToInformationCategoryId(id);
    if (isNominationCategory(category)) setNominationCategoryId(id);
    if (isExtraditionMlaCategory(category)) setExtraditionMlaCategoryId(id);
    if (isCivilAppellateCategory(category)) setCivilAppellateCategoryId(id);
  }
}

export function wizardStepPath(_categoryId: string, step: number, caseRequestId?: string): string {
  const base = `${WIZARD_BASE}/step-${String(step)}`;
  const id = (caseRequestId ?? '').trim();
  return id ? `${base}/${id}` : base;
}

export function wizardReviewPath(_categoryId?: string, caseRequestId?: string): string {
  const base = `${WIZARD_BASE}/review`;
  const id = (caseRequestId ?? '').trim();
  return id ? `${base}/${id}` : base;
}

/**
 * Whether file/sub file category controls are editable in the create wizard.
 * Saved drafts (including duplicated cases) may change category; callers pass
 * `requestedEditable=false` on review/read-only surfaces.
 * `draftRequestId` is retained for call-site compatibility and is not used to lock.
 */
export function isWizardCategorizationEditable(
  requestedEditable: boolean,
  _draftRequestId?: string,
): boolean {
  return requestedEditable;
}

/** Cover note uses CMS-CRC-30 common endpoint for these flows. */
export function usesCommonCoverNote(flow: WizardFlowKind): boolean {
  return (
    flow === 'civil-advise' ||
    flow === 'civil-litigation' ||
    flow === 'civil-appellate' ||
    flow === 'nomination-requests' ||
    flow === 'extradition-mla' ||
    flow === 'attorney-general' ||
    flow === 'public-petition'
  );
}

/** Criminal appellate uses common cover-note API with a dedicated modal layout. */
export function usesCriminalAppellateCoverNote(flow: WizardFlowKind): boolean {
  return flow === 'criminal-appellate';
}
